import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const useAuth = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);

  // 检查认证状态 - 使用同步方式，不依赖API验证
  const checkAuthStatus = useCallback(() => {
    try {
      const token = localStorage.getItem('token');
      const userData = localStorage.getItem('user');
      
      if (token && userData) {
        // 尝试解析用户数据
        try {
          const parsedUser = JSON.parse(userData);
          // 直接信任本地存储的认证状态
          // 只有在token确实无效时才会被清除
          setIsAuthenticated(true);
          setUser(parsedUser);
        } catch (e) {
          // 数据解析失败，清除存储
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          setIsAuthenticated(false);
          setUser(null);
        }
      } else {
        setIsAuthenticated(false);
        setUser(null);
      }
    } catch (error) {
      console.error('检查登录状态失败:', error);
      setIsAuthenticated(false);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // 验证token的后台API调用（可选，用于获取最新用户信息）
  const verifyToken = useCallback(async () => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    
    if (!token || !userData) {
      return false;
    }
    
    try {
      // 使用一个不会被响应拦截器清除token的方式验证
      const response = await fetch('/api/users/verify', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.valid) {
          // 更新本地用户数据
          if (data.user) {
            localStorage.setItem('user', JSON.stringify(data.user));
            setUser(data.user);
          }
          setIsAuthenticated(true);
          return true;
        }
      }
      
      // token无效
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      setIsAuthenticated(false);
      setUser(null);
      return false;
    } catch (error) {
      // 网络错误时保留本地认证状态
      console.log('验证token时网络错误，使用本地缓存数据');
      return true;
    }
  }, []);

  // 登录成功
  const login = useCallback((token, userData) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    setIsAuthenticated(true);
    window.dispatchEvent(new Event('userLoggedIn'));
  }, []);

  // 退出登录
  const logout = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        // 调用后端退出接口
        await axios.post('/api/users/logout', {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
    } catch (error) {
      // 忽略退出接口错误
      console.log('退出登录接口调用失败:', error);
    } finally {
      // 无论接口是否成功，都清除本地存储
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      setUser(null);
      setIsAuthenticated(false);
      window.dispatchEvent(new Event('userLoggedOut'));
    }
  }, []);

  useEffect(() => {
    checkAuthStatus();

    // 监听用户登录事件
    const handleUserLoggedIn = () => {
      checkAuthStatus();
    };

    const handleUserLoggedOut = () => {
      setIsAuthenticated(false);
      setUser(null);
    };

    // 监听storage变化（同一浏览器不同标签页之间同步登录状态）
    const handleStorageChange = () => {
      checkAuthStatus();
    };

    window.addEventListener('userLoggedIn', handleUserLoggedIn);
    window.addEventListener('userLoggedOut', handleUserLoggedOut);
    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('userLoggedIn', handleUserLoggedIn);
      window.removeEventListener('userLoggedOut', handleUserLoggedOut);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [checkAuthStatus]);

  // 注：已移除verifyToken后台验证，避免接口不可用时清除有效token
  // checkAuthStatus已足够在页面刷新时恢复登录状态

  // 获取当前用户信息
  const getCurrentUser = useCallback(() => {
    if (!user) {
      const userData = localStorage.getItem('user');
      if (userData) {
        return JSON.parse(userData);
      }
    }
    return user;
  }, [user]);

  // 获取token
  const getToken = useCallback(() => {
    return localStorage.getItem('token');
  }, []);

  return { 
    isAuthenticated, 
    loading,
    user,
    login,
    logout,
    checkAuthStatus,
    getCurrentUser,
    getToken
  };
};

export default useAuth;